import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'
import { GROUP_TOTAL_ROW_LABEL } from '@modules/consolidator/constants/group-wallet'
import {
  resolvePosicaoBlockPlTotal,
  resolvePosicaoPlPorCliente,
} from '@modules/consolidator/utils/posicao-pl-fallback/posicao-pl-fallback'

/**
 * Monta as linhas da seção "Por Carteira" a partir do payload já adaptado.
 *
 * ★ NÃO CALCULA NENHUMA ESTATÍSTICA (AC-2.3). Só faz join (breakdown × membros),
 * ordenação e atribuição de cor. Rentabilidade, volatilidade, Sharpe e relativa
 * vêm prontos do backend:
 *
 * - as LINHAS DE MEMBRO vêm de `breakdown.data[id].statistics`, que o backend
 *   produz com `group_by=id_cliente` pelo mesmo pipeline de agregação por grupo;
 * - a linha TOTAL vem de `portfolio_statistics`, que é o resultado do grupo
 *   inteiro — nunca a soma nem a média das linhas de membro.
 *
 * ★ PL é a exceção deliberada, e vem de `posicao_completa` — não do breakdown.
 * A tela individual mostra a POSIÇÃO ATUAL (snapshot do banco, inclui ativo sem
 * série no S3); `breakdown.pl_fim` é o último ponto da série cotizada. Ler o
 * segundo aqui fazia a mesma carteira valer R$ 7,67 mi no grupo e R$ 9,09 mi na
 * tela do cliente. O particionamento por titular usa a MESMA função que produz
 * o total da tela individual, então as duas não têm como divergir.
 *
 * As duas razões calculadas aqui são `plPercent` (PL do membro ÷ PL do grupo) e
 * a soma de PL por titular: ambas operam sobre valores prontos e aditivos, não
 * sobre métrica.
 *
 * @param {Object} args
 * @param {Object} [args.breakdown] `{by, data}` do payload adaptado.
 * @param {Object} [args.portfolioStatistics] Statistics do grupo inteiro.
 * @param {Object} [args.posicaoCompleta] Bloco `posicao_completa` do payload.
 * @param {Array}  [args.membros] `[{id_cliente_app, nome, parentesco, incluido}]`.
 * @param {Array}  [args.palette] Paleta compartilhada com o donut (AC-4.5).
 * @returns {{rows: Array, totalRow: Object}}
 */
export const buildGroupWalletRows = ({
  breakdown,
  portfolioStatistics,
  posicaoCompleta,
  membros,
  palette,
} = {}) => {
  const colors = Array.isArray(palette) && palette.length ? palette : REPORT_PALETTE
  const membrosById = indexMembrosById(membros)
  const entries = readBreakdownEntries(breakdown)
  const posicao = buildPosicaoPl(posicaoCompleta)

  const rows = entries
    .map((entry) => buildMemberRow(entry, membrosById, posicao))
    .sort(byPlDesc)
    .map((row, index) => ({
      ...row,
      colorIndex: index,
      color: colors[index % colors.length],
    }))

  const totalRow = buildTotalRow(portfolioStatistics, posicao)
  return { rows: applyPlPercent(rows, totalRow.pl), totalRow }
}

/**
 * `disponivel: false` quando o chamador não pediu `posicao_completa` ao backend.
 * Aí membro e Total voltam juntos para a série cotizada — divergir da tela do
 * cliente é ruim, mas misturar as duas fontes na mesma tabela é pior: as linhas
 * deixariam de somar o Total.
 */
const buildPosicaoPl = (posicaoCompleta) => {
  const plTotal = resolvePosicaoBlockPlTotal(posicaoCompleta)
  if (plTotal === null) return { disponivel: false, plTotal: null, porCliente: {} }
  return {
    disponivel: true,
    plTotal,
    porCliente: resolvePosicaoPlPorCliente(posicaoCompleta),
  }
}

/**
 * Linhas de PL por membro montadas SÓ do `posicao_completa`, sem passar pelo
 * breakdown. É o que alimenta o donut "Distribuição por Conta".
 *
 * O donut não pode depender de `group_by`: o breakdown vem na dimensão que o
 * usuário escolheu em "Agrupar por", então com Subclasse (o padrão) não existe
 * nenhuma entrada por membro e o donut zerava — "Sem dados no momento" numa tela
 * cheia de dado. `posicao_completa` é particionada por titular em qualquer
 * agrupamento, que é justamente o que o donut precisa.
 *
 * Sem estatística: só PL, percentual e cor. Quem precisa de rentabilidade e
 * volatilidade por membro é a seção "Por Carteira", e ela continua exigindo o
 * breakdown por membro — inventar métrica a partir do PL seria pior que o vazio.
 *
 * @param {Object} args
 * @param {Object} [args.posicaoCompleta] Bloco `posicao_completa` do payload.
 * @param {Array}  [args.membros] `[{id_cliente_app, nome}]`.
 * @param {Array}  [args.palette] Mesma paleta da tabela (AC-4.5).
 * @returns {Array} Linhas `{id, nome, pl, plPercent, color, colorIndex}`.
 */
export const buildGroupMemberPlRows = ({
  posicaoCompleta,
  membros,
  palette,
} = {}) => {
  const colors = Array.isArray(palette) && palette.length ? palette : REPORT_PALETTE
  const posicao = buildPosicaoPl(posicaoCompleta)
  if (!posicao.disponivel) return []

  const membrosById = indexMembrosById(membros)
  const rows = Object.keys(posicao.porCliente)
    .map((id) => ({
      id,
      nome: (membrosById[id] && membrosById[id].nome) || id,
      pl: posicao.porCliente[id],
      // `applyPlPercent` devolve as linhas intactas quando o PL do grupo e 0 ou
      // nulo; sem este default o donut receberia `undefined` em vez de `null`.
      plPercent: null,
    }))
    .sort(byPlDesc)
    .map((row, index) => ({
      ...row,
      colorIndex: index,
      color: colors[index % colors.length],
    }))

  return applyPlPercent(rows, posicao.plTotal)
}

/**
 * Converte as linhas no `chartData` do donut "Distribuição por Conta".
 *
 * O donut ordena por percentual decrescente e colore pelo índice na mesma
 * `REPORT_PALETTE`; como as linhas já vêm ordenadas por PL decrescente, as cores
 * saem sincronizadas entre tabela e donut sem nenhum acoplamento extra (AC-4.5).
 *
 * @param {Array} rows Linhas de `buildGroupWalletRows`.
 * @returns {Object} `{conta_percent, conta_sum_auc}`.
 */
export const buildGroupAccountDistribution = (rows) => {
  const percent = {}
  const sumAuc = {}
  if (!Array.isArray(rows)) return { conta_percent: percent, conta_sum_auc: sumAuc }

  for (const row of rows) {
    const label = resolveDistributionLabel(row, percent)
    percent[label] = row.plPercent === null ? 0 : row.plPercent
    sumAuc[label] = row.pl === null ? 0 : row.pl
  }

  return { conta_percent: percent, conta_sum_auc: sumAuc }
}

// Dois membros podem ter o mesmo nome; a chave do donut é o rótulo, então
// desempatamos com o id para não perder uma fatia silenciosamente.
const resolveDistributionLabel = (row, taken) => {
  const nome = row.nome || String(row.id)
  return taken[nome] === undefined ? nome : `${nome} (${row.id})`
}

const indexMembrosById = (membros) => {
  const byId = {}
  if (!Array.isArray(membros)) return byId
  for (const membro of membros) {
    if (!membro || membro.id_cliente_app === undefined) continue
    if (membro.id_cliente_app === null) continue
    byId[String(membro.id_cliente_app)] = membro
  }
  return byId
}

const readBreakdownEntries = (breakdown) => {
  const data = breakdown && breakdown.data
  if (!data || typeof data !== 'object') return []
  return Object.keys(data).map((id) => ({ id, group: data[id] || {} }))
}

/**
 * Membro presente em `membros[]` mas ausente do breakdown não vira linha — ele
 * aparece na Barra de Confiança, com o motivo da exclusão.
 */
const buildMemberRow = ({ id, group }, membrosById, posicao) => {
  const membro = membrosById[id] || {}
  const statistics = group.statistics || {}

  return {
    id,
    nome: membro.nome || id,
    parentesco: membro.parentesco || '',
    pl: resolveMemberPl(id, group, statistics, posicao),
    plPercent: null,
    rentabilidade: readNumber(statistics.inicio),
    volatilidade: readNumber(statistics.vol),
    rentRelativa: readNumber(statistics.inicio_relativo),
    rent12m: readNumber(statistics['12M']),
    sharpe: readNumber(statistics.sharp_ratio),
    inicio: statistics.data_inicio || null,
    colorIndex: 0,
    color: '',
    isTotal: false,
  }
}

/**
 * Membro sem nenhum ativo na posição vale zero, não `pl_fim`: com a posição
 * disponível, ela é a única fonte da coluna — senão as linhas param de somar o
 * Total, que é o sintoma que o consultor enxerga primeiro.
 */
const resolveMemberPl = (id, group, statistics, posicao) => {
  if (!posicao.disponivel) return readNumber(group.pl_fim, statistics.pl)
  const daPosicao = posicao.porCliente[String(id)]
  return daPosicao === undefined ? 0 : daPosicao
}

const buildTotalRow = (portfolioStatistics, posicao) => {
  const statistics = portfolioStatistics || {}
  return {
    id: 'total',
    nome: GROUP_TOTAL_ROW_LABEL,
    parentesco: '',
    pl: posicao.disponivel ? posicao.plTotal : readNumber(statistics.pl),
    plPercent: null,
    rentabilidade: readNumber(statistics.inicio),
    volatilidade: readNumber(statistics.vol),
    rentRelativa: readNumber(statistics.inicio_relativo),
    rent12m: readNumber(statistics['12M']),
    sharpe: readNumber(statistics.sharp_ratio),
    inicio: statistics.data_inicio || null,
    colorIndex: -1,
    color: '',
    isTotal: true,
  }
}

const applyPlPercent = (rows, plTotal) => {
  const total = readNumber(plTotal)
  if (total === null || total === 0) return rows
  return rows.map((row) => ({
    ...row,
    plPercent: row.pl === null ? null : row.pl / total,
  }))
}

const byPlDesc = (a, b) => (b.pl === null ? 0 : b.pl) - (a.pl === null ? 0 : a.pl)

// NaN nunca chega até aqui (o adapter sanitiza), mas o backend pode mandar
// `null` legítimo — e `null` significa "não há dado", não zero.
const readNumber = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate
    }
  }
  return null
}
